import { loadCountryDataset } from "@/data/country";
import type { CountryDataset, SupportedCountry } from "@/types/onboarding";

/**
 * Country data service. UI components never import a dataset directly — they
 * ask this service, which loads a country on demand and caches the result for
 * the session. Search is done here too so components stay presentational.
 */
const cache = new Map<SupportedCountry, Promise<CountryDataset>>();

export function getCountryData(iso: SupportedCountry): Promise<CountryDataset> {
  const cached = cache.get(iso);
  if (cached) return cached;
  const promise = loadCountryDataset(iso).catch((error) => {
    cache.delete(iso);
    throw error;
  });
  cache.set(iso, promise);
  return promise;
}

export const countryQueryKey = (iso: SupportedCountry) => ["country-data", iso] as const;

function match(value: string, term: string) {
  return value.toLowerCase().includes(term.toLowerCase());
}

export function searchList(values: string[], term: string, limit = 60): string[] {
  const query = term.trim();
  if (!query) return values.slice(0, limit);
  return values.filter((value) => match(value, query)).slice(0, limit);
}

export function regionNames(data: CountryDataset): string[] {
  return data.regions.map((region) => region.name);
}

export function citiesForRegion(data: CountryDataset, regionName: string): string[] {
  const region = data.regions.find((item) => item.name === regionName);
  if (region) return region.cities;
  // No region chosen yet — offer every city in the country.
  return data.regions.flatMap((item) => item.cities).sort((a, b) => a.localeCompare(b));
}
